import { IRow, ISort } from "@components/table";
import { ICompanyEntity, IGrantEntity } from "@odata/GeneratedEntityTypes";
import { logger } from "@utils/log";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { BreadCrumbProvider } from "../../../components/breadCrumb";
import ViewHeader from "../../../components/smart/smartHeader/SmartHeader";
import TableWithAutoSizedColumns from "../../../components/table/TableWithAutoSizedColumns";
import { DASHBOARD_DATA_API } from "../../../constants";
import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { Sort, TextAlign } from "../../../enums";
import { ROUTE_HOME, ROUTE_NONPROFIT_GRANT } from "../../../routes";
import customFetch from "../../../utils/customFetch";
import { TableWrapper } from "../../../views/table/TableView.styles";
import View from "../../../views/View";
import { TRecordAny } from "../../../global.types";
import { formatCurrency } from "../../../types/Currency";
import { CurrencyCode } from "@odata/GeneratedEnums";
import i18next from "i18next";
import DateType from "../../../types/Date";
import { ILinkedPagesState } from "./linkedPagesUtils";
import { sortCompareFn } from "@utils/general";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";

interface IProps extends WithTranslation {
}

interface IState extends ILinkedPagesState {
    loading: boolean;
    data?: TRecordAny[];
}

const GRANT_OVERVIEW_URL = `${DASHBOARD_DATA_API}/CrossCompanyOpenGrants`;

class GrantsOverview extends React.Component<IProps, IState> {
    static contextType = AppContext;
    isBcSet = false;

    state: IState = {
        loading: true,
        sort: [{
            id: "GrantShortName",
            sort: Sort.Asc
        }]
    };

    async componentDidMount() {
        this.load();
        this.setBreadcrumbs();
    }

    componentDidUpdate() {
        this.setBreadcrumbs();
    }

    setBreadcrumbs = () => {
        if (this.props.tReady && !this.isBcSet) {
            this.context.setViewBreadcrumbs({
                items: [{
                    key: "DashBoard",
                    title: this.props.t("Home:Grant.Home"),
                    link: ROUTE_HOME
                }, {
                    key: "grants",
                    title: this.props.t("Home:Grant.TableHeading"),
                }],
                lockable: false
            });
            this.isBcSet = true;
        }
    }

    get columns() {
        const { t } = this.props;

        return [
            { id: "CompanyName", label: t("Home:Grant.Company"), textAlign: TextAlign.Left, width: 200 },
            { id: "GrantShortName", label: t("Home:Grant.CrossName") },
            { id: "GrantDeadlineForSettling", label: t("Home:Grant.Deadline") },
            { id: "GrantAmount", label: t("Home:Grant.Amount"), textAlign: TextAlign.Right },
            { id: "GrantBalanceAmount", label: t("Home:Grant.BalanceAmount"), textAlign: TextAlign.Right },
        ];
    }

    load = async (): Promise<void> => {
        this.setState({ loading: true });
        let data: TRecordAny[];
        try {
            const res = await customFetch(GRANT_OVERVIEW_URL);
            data = await res.json() as TRecordAny[];
        } catch (e) {
            logger.error(e);
        }

        this.setState({ loading: false, data });
    }

    getSortedRows(): IRow[] {
        const { sort, data } = this.state;
        const companies = this.context.getData().companies;

        if (!data) {
            return [];
        }

        const sortBy = sort[0].id as string;
        const sortedData = data?.sort((row1: TRecordAny, row2: TRecordAny) => {
            const valueA = row1[sortBy];
            const valueB = row2[sortBy];
            return sortCompareFn(valueA, valueB, sort[0].sort);
        });

        return sortedData?.map(row => ({
            id: row.GrantId,
            values: {
                CompanyName: companies.find((c: ICompanyEntity) => c.Id === row.CompanyId)?.Name,
                GrantShortName: getTableDrillDownLink(row.GrantShortName, {
                    route: `${ROUTE_NONPROFIT_GRANT}/${row.GrantId}`,
                    context: this.context,
                    companyId: row.CompanyId
                }),
                GrantDeadlineForSettling: DateType.format(row.GrantDeadlineForSettling),
                GrantAmount: formatCurrency(row.GrantAmount, CurrencyCode.CzechKoruna),
                GrantBalanceAmount: formatCurrency(row.GrantBalanceAmount, CurrencyCode.CzechKoruna)
            }
        }));
    }

    handleSort = (sort: ISort[]): void => {
        this.setState({ sort });
    };

    render() {
        if (!this.props.tReady) {
            return null;
        }

        return (
            <>
                <BreadCrumbProvider back={ROUTE_HOME}/>
                <View hotspotContextId={"crossgrants"}>
                    <ViewHeader title={this.props.t("Home:Grant.TableHeading")}
                                shouldHideVariant/>
                    <TableWrapper>
                        <TableWithAutoSizedColumns
                            busy={this.state.loading}
                            rows={this.getSortedRows()}
                            sort={this.state.sort}
                            onSortChange={this.handleSort}
                            columns={this.columns}/>
                    </TableWrapper>
                </View>
            </>
        );
    }
}

export default withTranslation(["Home"])(GrantsOverview);